import React, { useCallback, useMemo, useState } from "react";

import { DateRange } from "react-day-picker";
import { Link } from "react-router-dom";

import { ExternalLink, Search } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Input } from "~/components/ui/input";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { useUserProfiles } from "~/hooks/useUserProfiles";
import { supabase } from "~/integrations/supabase/client";
import TablePagination from "~/pages/payments/components/TablePagination";
import { usePaymentsData } from "~/pages/payments/hooks/usePaymentsData";
import OrderDetailDialog from "~/pages/purchase-orders/OrderDetailDialog";
import { usePayments } from "~/pages/purchase-orders/hooks/usePayments";
import { SalesRequest, SalesRequestItem } from "~/types/client";

interface PaymentsHistoryTableProps {
  dateRange?: DateRange;
  paymentMethod?: string;
  bank?: string;
}

const PaymentsHistoryTable: React.FC<PaymentsHistoryTableProps> = ({
  dateRange,
  paymentMethod,
  bank,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");

  // Use the updated hook with pagination and search
  const {
    payments: allPayments,
    totalCount,
    isLoading,
  } = usePaymentsData(
    dateRange,
    {
      page: currentPage,
      itemsPerPage,
    },
    {
      searchTerm: searchTerm.trim() || undefined,
      paymentMethod,
      bank,
    }
  );

  // Filter to show only verified payments
  const payments = useMemo(() => {
    return (
      allPayments?.filter(
        (payment) =>
          payment.verified_at !== null && payment.verified_at !== undefined
      ) || []
    );
  }, [allPayments]);

  const totalPages = Math.ceil(totalCount / itemsPerPage);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };
  const [selectedSalesRequest, setSelectedSalesRequest] =
    useState<SalesRequest | null>(null);
  const [clientNames, setClientNames] = useState<Record<string, string>>({});
  const [loadingClientName, setLoadingClientName] = useState(false);
  const [sellerInfo, setSellerInfo] = useState<{
    name: string;
    lastname: string;
    email: string;
  }>({
    name: "Vendedor",
    lastname: "",
    email: "",
  });
  const [salesRequestItems, setSalesRequestItems] = useState<
    SalesRequestItem[]
  >([]);

  // Use the payments hook to get payment data for the selected sales request
  const {
    payments: orderPayments,
    loading: loadingPayments,
    refresh: refreshPayments,
  } = usePayments(selectedSalesRequest?.id || null);

  // Extract user IDs for fetching profiles
  const userIds = useMemo(() => {
    if (!payments) return [];
    return payments
      .map((payment) => payment.created_by)
      .filter(Boolean) as string[];
  }, [payments]);

  // Fetch user profiles
  const { getUserName } = useUserProfiles(userIds);

  // Function to view payment proof
  const viewPaymentProof = (imageUrl: string) => {
    window.open(imageUrl, "_blank");
  };

  // Function to fetch seller info
  const fetchSellerInfo = useCallback(async (sellerId: string) => {
    try {
      const { data, error } = await supabase
        .from("profiles")
        .select("name, lastname, email")
        .eq("id", sellerId)
        .single();

      if (error) throw error;

      if (data) {
        setSellerInfo({
          name: data.name || "Vendedor",
          lastname: data.lastname || "",
          email: data.email || "",
        });
      }
    } catch (error) {
      console.error("Error fetching seller info:", error);
    }
  }, []);

  // Function to fetch sales request items
  const fetchSalesRequestItems = useCallback(async (salesRequestId: string) => {
    try {
      const { data, error } = await supabase
        .from("sales_request_items")
        .select("*")
        .eq("sales_request_id", salesRequestId);

      if (error) throw error;

      if (data) {
        const items: SalesRequestItem[] = data.map((item) => ({
          id: item.id,
          salesRequestId: item.sales_request_id,
          productId: item.product_id,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          discountPercent: item.discount_percent ?? undefined,
        }));
        setSalesRequestItems(items);
      }
    } catch (error) {
      console.error("Error fetching sales request items:", error);
      setSalesRequestItems([]);
    }
  }, []);

  // Function to view sales request details
  const viewSalesRequest = async (salesRequest: any) => {
    if (!salesRequest) return;

    // Convert to SalesRequest format
    const convertedRequest: SalesRequest = {
      id: salesRequest.id,
      clientId: salesRequest.client_id,
      createdBy: salesRequest.created_by,
      approvedRejectedBy: salesRequest.approved_rejected_by,
      status: salesRequest.status,
      paid: salesRequest.paid,
      paymentTerms: salesRequest.payment_terms,
      paymentType: salesRequest.payment_type as "future" | "prepaid",
      paymentDueDate: salesRequest.payment_due_date,
      totalAmount: salesRequest.total_amount,
      comments: salesRequest.comments || undefined,
      deliveryDate: salesRequest.delivery_date || undefined,
      deliveryAddressId: salesRequest.delivery_address_id || undefined,
      deliveryStreet: salesRequest.delivery_street || undefined,
      deliveryNumber: salesRequest.delivery_number || undefined,
      deliveryComplement: salesRequest.delivery_complement || undefined,
      deliveryCity: salesRequest.delivery_city || undefined,
      deliveryState: salesRequest.delivery_state || undefined,
      deliveryComuna: salesRequest.delivery_comuna || undefined,
      createdAt: salesRequest.created_at,
      updatedAt: salesRequest.updated_at,
      rejectedReason: salesRequest.rejected_reason || undefined,
    };

    // Fetch client name if not already available
    if (!clientNames[convertedRequest.clientId]) {
      await fetchClientName(convertedRequest.clientId);
    }

    // Fetch seller info
    if (convertedRequest.createdBy) {
      await fetchSellerInfo(convertedRequest.createdBy);
    }

    // Fetch sales request items
    await fetchSalesRequestItems(convertedRequest.id);

    setSelectedSalesRequest(convertedRequest);
  };

  // Function to fetch client name
  const fetchClientName = useCallback(async (clientId: string) => {
    setLoadingClientName(true);
    try {
      const { data, error } = await supabase
        .from("clients")
        .select("id, name")
        .eq("id", clientId)
        .single();

      if (error) throw error;

      if (data) {
        setClientNames((prev) => ({
          ...prev,
          [data.id]: data.name,
        }));
      }
    } catch (error) {
      console.error("Error fetching client name:", error);
    } finally {
      setLoadingClientName(false);
    }
  }, []);

  // Function to get seller info for the OrderDetailDialog
  const getSellerInfo = useCallback(
    (id: string) => {
      return sellerInfo;
    },
    [sellerInfo]
  );

  // Function to get approver info for the OrderDetailDialog
  const getApproverRejecterInfo = useCallback((id?: string) => {
    return {
      name: "Sistema",
      lastname: "",
      email: "",
      role: "system",
    };
  }, []);

  return (
    <Card>
      <CardHeader className="space-y-4">
        <div>
          <CardTitle>Historial de Pagos</CardTitle>
          <CardDescription>
            Lista de todos los pagos registrados en el sistema
          </CardDescription>
        </div>
        <div className="relative w-full md:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por cliente, RUT o referencia..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="h-9 w-full pl-9"
          />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={8} columns={6} />
          </div>
        ) : payments && payments.length > 0 ? (
          <>
            {/* Mobile: stacked card list */}
            <div className="space-y-3 p-4 md:hidden">
              {payments.map((payment) => (
                <div key={payment.id} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      {payment.client?.name ? (
                        <Link
                          to={`/clients/${payment.client}`}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {payment.client.name}
                        </Link>
                      ) : (
                        <span className="font-medium">No especificado</span>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {payment.client?.tax_id || "Sin RUT"}
                      </p>
                    </div>
                    <span className="whitespace-nowrap font-semibold">
                      ${payment.amount?.toLocaleString() || 0}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {payment.verified_at
                      ? new Date(payment.verified_at).toLocaleString("es-CL", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: false,
                        })
                      : "No verificado"}
                  </div>
                  <div className="mt-2 flex items-center justify-between gap-2">
                    {payment.sales_request ? (
                      <Button
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-blue-500 hover:text-blue-700"
                        onClick={() => viewSalesRequest(payment.sales_request)}
                      >
                        <span className="inline-block max-w-[140px] truncate align-bottom">
                          {payment.sales_request.id.substring(0, 8)}...
                        </span>
                        <ExternalLink className="ml-1 inline h-3 w-3" />
                      </Button>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                    {payment.created_by ? (
                      <span className="text-xs font-medium text-blue-600">
                        {getUserName(payment.created_by)}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400">Sistema</span>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block">
              <Table>
                <TableCaption>Historial de pagos</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead className="whitespace-nowrap">
                      Fecha y Hora
                    </TableHead>
                    <TableHead className="whitespace-nowrap">Cliente</TableHead>
                    <TableHead className="whitespace-nowrap">RUT</TableHead>
                    <TableHead className="whitespace-nowrap text-right">
                      Monto
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      Nota de Venta
                    </TableHead>
                    <TableHead className="whitespace-nowrap">
                      Registrado por
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {payments.map((payment, index) => (
                    <TableRow
                      key={payment.id}
                      style={{
                        animationDelay: `${Math.min(index * 40, 400)}ms`,
                      }}
                      className="duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both"
                    >
                      <TableCell className="whitespace-nowrap">
                        {payment.verified_at
                          ? new Date(payment.verified_at).toLocaleString(
                              "es-CL",
                              {
                                year: "numeric",
                                month: "short",
                                day: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: false,
                              }
                            )
                          : "No verificado"}
                      </TableCell>
                      <TableCell>
                        {payment.client?.name ? (
                          <Link
                            to={`/clients/${payment.client}`}
                            className="text-blue-600 hover:underline"
                          >
                            {payment.client.name}
                          </Link>
                        ) : (
                          "No especificado"
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {payment.client?.tax_id || "No especificado"}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right">
                        ${payment.amount?.toLocaleString() || 0}
                      </TableCell>
                      <TableCell>
                        {payment.sales_request ? (
                          <Button
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-blue-500 hover:text-blue-700"
                            onClick={() =>
                              viewSalesRequest(payment.sales_request)
                            }
                          >
                            <span className="inline-block max-w-[120px] truncate align-bottom">
                              {payment.sales_request.id.substring(0, 8)}...
                            </span>
                            <ExternalLink className="ml-1 inline h-3 w-3" />
                          </Button>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        {payment.created_by ? (
                          <span className="text-xs font-medium text-blue-600">
                            {getUserName(payment.created_by)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Sistema</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        ) : (
          <div className="py-10 text-center text-sm text-muted-foreground">
            No hay pagos registrados
          </div>
        )}

        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalCount}
          itemsPerPage={itemsPerPage}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          isLoading={isLoading}
        />
      </CardContent>

      {/* Sales Request Detail Dialog */}
      {selectedSalesRequest && (
        <OrderDetailDialog
          selectedOrder={selectedSalesRequest}
          setSelectedOrder={setSelectedSalesRequest}
          getSellerInfo={getSellerInfo}
          getApproverRejecterInfo={getApproverRejecterInfo}
          clientNames={{
            [selectedSalesRequest.clientId]:
              clientNames[selectedSalesRequest.clientId] || "Cliente",
          }}
          items={salesRequestItems}
          onOrderStatusChange={() => {}}
        />
      )}
    </Card>
  );
};

export default PaymentsHistoryTable;
